/**
 * The settings page — docs/design-handoff/README.md §6, task-18-brief.md.
 * The single editable surface for every deployment- and brand-specific
 * value (communicatorv2.3.md §8: runtime-hot / build-time / infra-time,
 * nothing hardcoded past those three tiers). Section nav + scrolling
 * content, field rows dispatched by kind in components/settings/FieldRow.
 *
 * Writes go through components/settings/configClient.ts's `configWrite`
 * (`PUT /ops/config`); the layer itself is the enforcement point (task-18
 * requirement #4) — a refusal's own `OpsError.code`/`.message` is shown
 * inline rather than an invented message, and this page only renders
 * tenant fields inert without Admin as a courtesy shortcut, never as the
 * real gate.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ConfigField } from '@communicator/shared'
import { ops, OpsError } from '../lib/ops'
import { useStore } from '../store'
import { Card, Chip, Icon } from '../components/ui'
import {
  FieldRow,
  SectionNav,
  UsersSection,
  LinesSection,
  AliasesSection,
  AppDownloads,
  buildSections,
  configWrite,
  OPEN_DECISION_FIELD_IDS,
  sectionWiring,
  tierLabel,
} from '../components/settings'
import type { FieldsSettingsSection, SettingsSection } from '../components/settings'
import { PushDeviceRow } from '../components/settings/PushDeviceRow'
import { useIsMobile } from '../lib/viewport'
import { requestDesktopPermission } from '../lib/notifications'

/**
 * The "NOT WIRED · <TIER>" chip only makes sense for a `'fields'` section —
 * `tier` describes how a deployment VALUE takes effect, and a `'custom'`
 * section (Users) has neither fields nor a tier to report on (sections.ts's
 * own header comment). Taking a narrowed `FieldsSettingsSection` as a prop,
 * rather than inlining this at each call site, is what lets TypeScript
 * actually narrow `activeSection` at the two ternaries below instead of
 * losing that narrowing inside a closure.
 */
function SectionWiringChip({ section }: { section: FieldsSettingsSection }) {
  const wiring = sectionWiring(section.fields.map((f) => f.id))
  const tier = tierLabel(section.tier)
  if (wiring === 'all') return <Chip tone="neutral">{tier}</Chip>
  if (wiring === 'some') return <Chip tone="neutral">SOME FIELDS NOT WIRED · {tier}</Chip>
  return <Chip tone="neutral">NOT WIRED · {tier}</Chip>
}

/** Whether the active section is one of the two that hosts a genuinely-undecided field (`r.window`/`r.access` in `retention`, `i.risk` in `identity`) — see `OpenDecisionsNotice` below. */
function sectionHasOpenDecision(section: SettingsSection): boolean {
  return section.body === 'fields' && section.fields.some((f) => OPEN_DECISION_FIELD_IDS.includes(f.id))
}

/**
 * README §7's gold notice card, verbatim copy: "Three decisions are still
 * open — how long transcripts are kept, who may read a closed one, and
 * whether the highest-risk actions demand crypto." (An earlier revision of
 * the design said two; `sections.ts`'s own `OPEN_DECISION_FIELD_IDS` comment
 * explains why `r.access` is the third.) One fixed sentence about all three,
 * not a per-section count — the per-cluster gold badge in `SectionNav`
 * (`clusterOpenDecisionCount`) is the derived, honest count; this card is
 * the design's own fixed explanatory text, shown wherever one of the three
 * fields actually lives (retention, identity) so it reads next to the
 * "Undecided" row it is explaining rather than only in one place.
 */
function OpenDecisionsNotice() {
  return (
    <div
      data-testid="open-decisions-notice"
      className="mb-4 flex items-start gap-2 rounded-card px-4 py-3 text-[12.5px] text-hi"
      style={{ background: 'var(--tint-accent)', border: '1px solid var(--brand-accent)' }}
    >
      <span className="mt-0.5 flex-none" style={{ color: 'var(--on-tint-accent)' }}>
        <Icon name="triangle-alert" size={16} />
      </span>
      <p>
        <strong>Three</strong> decisions are still open — how long transcripts are kept, who may read a closed one,
        and whether the highest-risk actions demand crypto.
      </p>
    </div>
  )
}

/**
 * How wide a settings section is allowed to get.
 *
 * 720px is a READING width, and it is right for the field-row sections: a
 * label, a control and a hint, where a longer line is a worse line. The Users
 * section is not that shape — it is a data table of five columns, and holding
 * it to a reading width is what made its cells collapse on top of each other
 * when Extension was added (see UsersSection's own grid comment for the
 * arithmetic). Keyed on `body`, not on the section's name, so any future table
 * section gets the right width without anyone remembering to add it here.
 */
export function sectionMaxWidth(section: { body: string }): string {
  return section.body === 'custom' ? 'max-w-[1040px]' : 'max-w-[720px]'
}

export function SettingsPage() {
  const navigate = useNavigate()
  const admin = useStore((s) => s.admin)
  const cfg = useStore((s) => s.cfg)
  const setCfg = useStore((s) => s.setCfg)
  const set = useStore((s) => s.set)
  const settingsTab = useStore((s) => s.settingsTab)

  const sections = useMemo(() => buildSections(), [])
  const [errors, setErrors] = useState<Record<string, string | null>>({})
  const setCfgEnv = useStore((s) => s.setCfgEnv)
  const cfgEnv = useStore((s) => s.cfgEnv)
  const prefs = useStore((s) => s.prefs)
  const setPref = useStore((s) => s.setPref)

  useEffect(() => {
    let cancelled = false
    ops
      .config()
      .then((result) => {
        if (cancelled) return
        setCfg(result.values)
        if (result.env) setCfgEnv(result.env)
      })
      .catch(() => {
        // No config yet (offline, or the ops service isn't reachable): the
        // page still renders every section and field from CONFIG_FIELDS'
        // own defaults — nothing here fabricates a fetched value instead.
      })
    return () => {
      cancelled = true
    }
    // setCfg is a stable Zustand action reference; omitting it from deps
    // avoids re-fetching every time cfg itself changes downstream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const activeKey = sections.some((s) => s.key === settingsTab) ? settingsTab : (sections[0]?.key ?? '')
  const activeSection = sections.find((s) => s.key === activeKey)
  const isMobile = useIsMobile()
  // A phone starts on the list. `settingsTab` always holds a section, so it
  // cannot express "nothing open yet" on its own.
  const [mobileSectionOpen, setMobileSectionOpen] = useState(false)

  async function commitField(field: ConfigField, value: unknown) {
    const { id, gate, kind } = field
    if (gate === 'personal') {
      // Per-user, and now genuinely persisted: migration 005's
      // staff_preferences plus PUT /ops/preferences, scoped to the caller by
      // the service. Previously this kept the value in the shared store for
      // the session only, because /ops/config is one flat table keyed by field
      // id and a real write would have made one person's preference global.
      let value_ = value
      if (id === 'p.desktop' && value === true) {
        // Ask only when someone turns it ON. An unprompted permission dialog
        // at load is the most disliked thing a web app does, and Chrome
        // penalises origins that do it. If the browser refuses, save `false`
        // rather than a preference it will silently ignore — a toggle that
        // shows on while nothing arrives is exactly the kind of quiet lie this
        // whole pass is removing.
        const granted = await requestDesktopPermission()
        if (!granted) {
          value_ = false
          setErrors((prev) => ({
            ...prev,
            [id]: 'Your browser blocked notifications for this site. Allow them in the address-bar permissions, then turn this on again.',
          }))
        } else {
          setErrors((prev) => ({ ...prev, [id]: null }))
        }
      }
      setPref(id, value_)
      try {
        await ops.setPreference(id, value_)
      } catch (err) {
        setErrors((prev) => ({ ...prev, [id]: err instanceof Error ? err.message : 'Could not save that preference.' }))
      }
      return
    }
    try {
      await configWrite.write(id, value)
      // A secret's plaintext must have no lifetime in the client beyond the
      // request that carries it — task-18 fix-round-1 IMPORTANT #1. Writing
      // it into the shared `cfg` store would put it in inspectable
      // application state (devtools, a future store dump, an error
      // reporter, or a later component that iterates `cfg`), which defeats
      // the entire point of SecretField.tsx never rendering it back. The
      // presence indicator ("Set · replaced") lives entirely in
      // SecretField's own local state instead; `cfg[id]` for a secret field
      // is never touched by this page at all, on read or on write.
      if (kind !== 'secret') setCfg({ ...cfg, [id]: value })
      setErrors((prev) => ({ ...prev, [id]: null }))
    } catch (err) {
      const message = err instanceof OpsError ? err.message : err instanceof Error ? err.message : 'Save failed.'
      setErrors((prev) => ({ ...prev, [id]: message }))
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-page" data-testid="settings-page">
      <header className="flex flex-none flex-wrap items-center gap-3 border-b border-subtle px-4 py-3">
        <button
          type="button"
          onClick={() => {
            set({ page: null })
            navigate('/')
          }}
          className="text-[13px] text-mid hover:text-hi"
        >
          ← Back to the console
        </button>
        <h1 className="text-[15px] font-semibold text-hi">Settings</h1>
        <p className="text-[12px] text-lo">
          The single surface every deployment- and brand-specific value in this deployment reads from — nothing
          else holds a literal for a name, domain, colour, endpoint or credential.
        </p>
        <span className="ml-auto">
          <Chip tone={admin ? 'ok' : 'neutral'}>
            {admin ? 'Admin · tenant settings editable' : 'Not admin · tenant settings read-only'}
          </Chip>
        </span>
      </header>
      {/* TWO SCREENS ON A PHONE, the same shape as the thread rail: the list
          of sections, then one section full width with a way back. The desktop
          keeps its nav-beside-panel — squeezing that onto a phone is what made
          Settings unusable there. */}
      {isMobile ? (
        !mobileSectionOpen ? (
          <div data-testid="settings-mobile-list" className="flex min-h-0 flex-1">
            <SectionNav
              fullWidth
              activeKey={activeKey}
              onSelect={(key) => {
                set({ settingsTab: key })
                setMobileSectionOpen(true)
              }}
            />
          </div>
        ) : (
          <div data-testid="settings-mobile-section" className="flex min-h-0 flex-1 flex-col">
            <div className="flex flex-none items-center gap-2 border-b border-subtle bg-sidebar px-3 py-2">
              <button
                type="button"
                data-testid="settings-mobile-back"
                aria-label="Back to settings"
                onClick={() => setMobileSectionOpen(false)}
                className="flex h-8 min-w-8 items-center justify-center rounded-[8px] border border-subtle bg-card px-2 text-[15px] text-hi"
              >
                ‹
              </button>
              <span className="truncate text-[13px] font-semibold text-hi">{activeSection?.label ?? 'Settings'}</span>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4" data-testid="settings-content">
          {activeSection ? (
            <section data-testid={`settings-section-${activeSection.key}`} className={sectionMaxWidth(activeSection)}>
              <div className="mb-1 flex items-center gap-2">
                <h2 className="text-[14px] font-semibold text-hi">{activeSection.label}</h2>
                {/* Was an unconditional "NOT WIRED" on every section, left
                    over from when that was true of all of them. It kept
                    claiming it after Halo, the timezone, business hours and
                    the SMS number were wired — which teaches people their
                    changes do nothing, so they stop making them. Only a
                    'fields' section has a tier to report wiring against at
                    all (SectionWiringChip's own doc comment). */}
                {activeSection.body === 'fields' ? <SectionWiringChip section={activeSection} /> : null}
              </div>
              <p className="mb-4 text-[12px] text-lo">{activeSection.blurb}</p>
              {activeSection.body === 'fields' && sectionHasOpenDecision(activeSection) ? <OpenDecisionsNotice /> : null}
              <div>
                {activeSection.body === 'fields' ? (
                  // README §7: "field rows move ... into grouped cards: one
                  // card per section." FieldRow itself keeps its own
                  // `border-b border-subtle` separators (FIELD_ROW_GRID_CLASS,
                  // asserted verbatim by test/settings.test.tsx's row-alignment
                  // suite) — this Card only supplies the card boundary around
                  // them, not a second separator system.
                  <Card className="px-4">
                    {/* Device registration sits with the personal preferences it
                        belongs beside, but is not one of them: `p.push` follows the
                        person everywhere, this browser's subscription does not. */}
                    {activeSection.key === 'personal' && <PushDeviceRow />}
                    {activeSection.fields.map((field) => (
                      <FieldRow
                        key={field.id}
                        field={field}
                        value={field.gate === 'personal' ? prefs[field.id] : cfg[field.id]}
                        admin={admin}
                        env={cfgEnv[field.id]}
                        error={errors[field.id] ?? null}
                        onCommit={(_id, value) => commitField(field, value)}
                      />
                    ))}
                  </Card>
                ) : (
                  // The 'custom'-body sections (sections.ts) — a live staff
                  // directory, a live line roster, or a live alias table, not
                  // CONFIG_FIELDS data, so each renders its own component
                  // instead of a FieldRow list. Keyed on the section itself,
                  // exactly like every 'fields' section already dispatches on
                  // its own fields.
                  activeSection.key === 'lines' ? (
                    <LinesSection />
                  ) : activeSection.key === 'aliases' ? (
                    <AliasesSection />
                  ) : activeSection.key === 'apps' ? (
                    <AppDownloads />
                  ) : (
                    <UsersSection />
                  )
                )}
              </div>
            </section>
          ) : null}
          <p className="mt-6 border-t border-subtle pt-4 text-[11px] text-lo">
            Runtime-hot changes take effect immediately, with no redeploy. Build-time values are compiled into
            the signed client binary and need a rebuild and repush before they show up anywhere. Infra-time
            values touch domains and TLS certificates.
          </p>
            </div>
          </div>
        )
      ) : (
        <div className="flex flex-1 min-h-0">
          <SectionNav activeKey={activeKey} onSelect={(key) => set({ settingsTab: key })} />
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5" data-testid="settings-content">
          {activeSection ? (
            <section data-testid={`settings-section-${activeSection.key}`} className={sectionMaxWidth(activeSection)}>
              <div className="mb-1 flex items-center gap-2">
                <h2 className="text-[14px] font-semibold text-hi">{activeSection.label}</h2>
                {/* Was an unconditional "NOT WIRED" on every section, left
                    over from when that was true of all of them. It kept
                    claiming it after Halo, the timezone, business hours and
                    the SMS number were wired — which teaches people their
                    changes do nothing, so they stop making them. Only a
                    'fields' section has a tier to report wiring against at
                    all (SectionWiringChip's own doc comment). */}
                {activeSection.body === 'fields' ? <SectionWiringChip section={activeSection} /> : null}
              </div>
              <p className="mb-4 text-[12px] text-lo">{activeSection.blurb}</p>
              {activeSection.body === 'fields' && sectionHasOpenDecision(activeSection) ? <OpenDecisionsNotice /> : null}
              <div>
                {activeSection.body === 'fields' ? (
                  // README §7: "field rows move ... into grouped cards: one
                  // card per section." FieldRow itself keeps its own
                  // `border-b border-subtle` separators (FIELD_ROW_GRID_CLASS,
                  // asserted verbatim by test/settings.test.tsx's row-alignment
                  // suite) — this Card only supplies the card boundary around
                  // them, not a second separator system.
                  <Card className="px-4">
                    {/* Device registration sits with the personal preferences it
                        belongs beside, but is not one of them: `p.push` follows the
                        person everywhere, this browser's subscription does not. */}
                    {activeSection.key === 'personal' && <PushDeviceRow />}
                    {activeSection.fields.map((field) => (
                      <FieldRow
                        key={field.id}
                        field={field}
                        value={field.gate === 'personal' ? prefs[field.id] : cfg[field.id]}
                        admin={admin}
                        env={cfgEnv[field.id]}
                        error={errors[field.id] ?? null}
                        onCommit={(_id, value) => commitField(field, value)}
                      />
                    ))}
                  </Card>
                ) : (
                  // The 'custom'-body sections (sections.ts) — a live staff
                  // directory, a live line roster, or a live alias table, not
                  // CONFIG_FIELDS data, so each renders its own component
                  // instead of a FieldRow list. Keyed on the section itself,
                  // exactly like every 'fields' section already dispatches on
                  // its own fields.
                  activeSection.key === 'lines' ? (
                    <LinesSection />
                  ) : activeSection.key === 'aliases' ? (
                    <AliasesSection />
                  ) : activeSection.key === 'apps' ? (
                    <AppDownloads />
                  ) : (
                    <UsersSection />
                  )
                )}
              </div>
            </section>
          ) : null}
          <p className="mt-6 border-t border-subtle pt-4 text-[11px] text-lo">
            Runtime-hot changes take effect immediately, with no redeploy. Build-time values are compiled into
            the signed client binary and need a rebuild and repush before they show up anywhere. Infra-time
            values touch domains and TLS certificates.
          </p>
          </div>
        </div>
      )}
    </div>
  )
}
